/* TaxRatesCrudCard.tsx
	Une card affichant la gestion CRUD des taux de taxe
	Utilise le composant GenericCrudCard pour lister, ajouter, éditer et supprimer les éléments
	Utilise listPaginated pour bénéficier de la pagination, du tri et des filtres côté serveur
*/

// Import React
import React from 'react';

// Import FontAwesome
import { faPercent } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

// Import Communs
import { GenericCrudCard } from 'common';

// Import Feature
import { TaxRateData, taxRatesApi } from 'features/taxRate';

// Interface des props du composant
interface TaxRatesCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
}

// TaxRatesCrudCard component
export const TaxRatesCrudCard: React.FC<TaxRatesCrudCardProps> = ({
	size = 'sm',
	returnPath = '/app/settings',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
}) => {
	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = size !== 'sm';
	// Titre de la card selon la taille
	const cardTitle = size === 'sm' ? 'Taux de taxe' : 'Liste des taux de taxe';

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "ID" uniquement si extended
		...(extended
			? [
					{
						label: 'ID',
						field: 'id',
						render: (a: TaxRateData) => a.id,
					},
				]
			: []),
		// Colonne "Code"
		{
			label: 'Code',
			field: 'code',
			render: (a: TaxRateData) => a.code,
		},
		// Colonne "Libellé"
		{
			label: 'Libellé',
			field: 'label',
			render: (a: TaxRateData) => a.label,
		},
		// Colonne "Taux" — affiché en pourcentage (rate * 100), tri possible sur la valeur brute
		{
			label: 'Taux',
			field: 'rate',
			render: (a: TaxRateData) => `${(a.rate * 100).toFixed(2)} %`,
		},
		// Colonne "Pays" uniquement si extended
		...(extended
			? [
					{
						label: 'Pays',
						field: 'country',
						render: (a: TaxRateData) => a.country ?? '—',
					},
				]
			: []),
		// Colonne "Actif" uniquement si extended — tri désactivé (valeur booléenne transformée en badge)
		...(extended
			? [
					{
						label: 'Actif',
						field: 'none',
						render: (a: TaxRateData) =>
							a.isActive ? (
								<span className="inline-block px-2 py-0.5 text-xs rounded-full bg-green-100 text-green-700 font-medium">
									Actif
								</span>
							) : (
								<span className="inline-block px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-500 font-medium">
									Inactif
								</span>
							),
					},
				]
			: []),
	];

	return (
		<GenericCrudCard<TaxRateData>
			queryKey="taxRates"
			title={cardTitle}
			fetchData={(token, options) => taxRatesApi.listPaginated(token, options)}
			deleteItem={taxRatesApi.delete}
			tableColumns={columns}
			addButtonLabel="Ajouter un taux de taxe"
			getAddPath={() => '/app/taxrates/new'}
			getEditPath={(id) => `/app/taxrates/${id}/edit`}
			emptyMessage="Aucun taux de taxe trouvé."
			getReturnPath={() => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' ? faPercent : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};

export default TaxRatesCrudCard;
